import React, { useEffect, useState } from "react";
import AppShell from "./AppShell";
import api from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar, Dumbbell, Clock, Repeat, Plus, BookOpen, X, History, Pencil, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const MUSCLE_LABEL = {
  espalda: "Espalda",
  pecho: "Pecho",
  hombros: "Hombros",
  biceps: "Bíceps",
  triceps: "Tríceps",
  abdomen: "Abdomen",
  gluteos: "Glúteos",
  cuadriceps: "Cuádriceps",
  femoral: "Femoral",
  pantorrillas: "Pantorrillas",
  piernas: "Piernas",
  cardio: "Cardio",
  calistenia: "Calistenia",
};

const LEVEL_COLOR = {
  principiante: "text-green-400 border-green-400/40 bg-green-400/10",
  intermedio: "text-[var(--mep-gold)] border-[var(--mep-gold)]/40 bg-[var(--mep-gold)]/10",
  avanzado: "text-[var(--mep-pink)] border-[var(--mep-pink)]/40 bg-[var(--mep-pink)]/10",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }); }
  catch { return "—"; }
}

function dayNameEs(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-ES", { weekday: "long" }); }
  catch { return ""; }
}

export default function RoutinesView({ sex, title, subtitle, muscleFilter, hideWeeklyPlan }) {
  const [data, setData] = useState(null);
  const [lastByExercise, setLastByExercise] = useState({}); // {exercise_id: entry|null}
  const [loggingFor, setLoggingFor] = useState(null); // exercise object or null
  const [customs, setCustoms] = useState([]);
  const [customForm, setCustomForm] = useState(null); // {muscle} | {id, name}
  const [deleting, setDeleting] = useState(null);

  const loadCustoms = () => api.get("/custom-exercises").then((r) => setCustoms(r.data)).catch(() => {});
  useEffect(() => { loadCustoms(); }, []);

  const removeCustom = async () => {
    try {
      await api.delete(`/custom-exercises/${deleting.id}`);
      toast.success("Ejercicio eliminado");
      setDeleting(null);
      loadCustoms();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  useEffect(() => {
    api.get(`/content/exercises/${sex}`).then((r) => setData(r.data));
  }, [sex]);

  // Lazy-load last sessions for each exercise (one bulk call via list endpoint)
  const refreshLastSessions = async () => {
    try {
      const r = await api.get("/training-log?limit=1000");
      const map = {};
      for (const e of r.data) {
        if (!map[e.exercise_id]) map[e.exercise_id] = e; // first = most recent (desc order)
      }
      setLastByExercise(map);
    } catch { /* silent */ }
  };
  useEffect(() => { refreshLastSessions(); }, []);

  if (!data) return <AppShell title={title}><div className="text-[var(--mep-text-muted)]">Cargando...</div></AppShell>;

  const official = muscleFilter
    ? data.exercises.filter((e) => e.muscle === muscleFilter)
    : data.exercises;
  const muscles = muscleFilter ? [muscleFilter] : [...new Set(official.map((e) => e.muscle))];
  const customCards = customs
    .filter((c) => muscles.includes(c.muscle))
    .map((c) => ({ ...c, sets: null, reps: "—", rest_sec: 0, bodyweight_default: c.muscle === "calistenia" }));
  const allExercises = [...official, ...customCards];

  return (
    <AppShell title={title} subtitle={subtitle || "Series, repeticiones, descanso y nivel para cada ejercicio."}>
      {/* Logbook CTA */}
      <div className="mb-6 flex flex-wrap gap-3 items-center">
        <Link
          to="/libreta"
          data-testid="routines-go-to-logbook"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--mep-gold)]/40 bg-[rgba(212,175,55,0.08)] text-sm text-[var(--mep-gold)] hover:bg-[rgba(212,175,55,0.15)] transition"
        >
          <BookOpen size={16} /> Ir a Mi libreta de entrenamiento
        </Link>
      </div>

      {/* Weekly plan */}
      {!hideWeeklyPlan && (
      <div className="mb-10 mep-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)]">
            <Calendar size={18} />
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight">Rutina semanal</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.weekly_plan.map((d) => (
            <div key={d.day} className="bg-[var(--mep-surface-2)] rounded-lg p-4 border border-[var(--mep-border)]">
              <div className="text-eyebrow">{d.day}</div>
              <div className="font-display text-base mt-2">{d.focus}</div>
            </div>
          ))}
        </div>
      </div>
      )}

      <Tabs defaultValue={muscles[0]}>
        {!muscleFilter && (
        <TabsList className="bg-[var(--mep-surface)] border border-[var(--mep-border)] flex-wrap h-auto">
          {muscles.map((m) => (
            <TabsTrigger
              key={m} value={m}
              data-testid={`routines-tab-${m}`}
              className="data-[state=active]:bg-[var(--mep-pink)] data-[state=active]:text-white"
            >
              {MUSCLE_LABEL[m] || m}
            </TabsTrigger>
          ))}
        </TabsList>
        )}

        {muscles.map((m) => (
          <TabsContent key={m} value={m}>
            <div className="mt-8 mb-6 flex items-center gap-4">
              <div className="h-px flex-1 bg-gradient-to-r from-[var(--mep-gold)] to-transparent" />
              <h3 className="font-display text-2xl md:text-3xl font-bold tracking-[0.18em] uppercase text-center">
                <span className="text-[var(--mep-pink)]">{MUSCLE_LABEL[m] || m}</span>
              </h3>
              <div className="h-px flex-1 bg-gradient-to-l from-[var(--mep-gold)] to-transparent" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allExercises.filter((e) => e.muscle === m).map((e, idx) => {
                const last = lastByExercise[e.id];
                return (
                  <article
                    key={e.id}
                    data-testid={`exercise-${e.id}`}
                    className="mep-card p-5"
                  >
                    <div className="flex gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <h4 className="font-display text-lg font-medium tracking-tight">{idx + 1}. {e.name}</h4>
                          {e.custom ? (
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] uppercase tracking-wider border rounded-full px-2 py-1 text-[var(--mep-text-muted)] border-[var(--mep-border)]" data-testid={`custom-badge-${e.id}`}>
                                Ejercicio personalizado
                              </span>
                              <button type="button" onClick={() => setCustomForm({ id: e.id, name: e.name })} data-testid={`custom-edit-${e.id}`} className="p-1 text-[var(--mep-text-muted)] hover:text-[var(--mep-gold)]" aria-label="Editar">
                                <Pencil size={14} />
                              </button>
                              <button type="button" onClick={() => setDeleting(e)} data-testid={`custom-delete-${e.id}`} className="p-1 text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)]" aria-label="Eliminar">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ) : (
                          <span className={`inline-block text-[10px] uppercase tracking-wider border rounded-full px-2 py-1 ${LEVEL_COLOR[e.level]}`}>
                            {e.level}
                          </span>
                          )}
                        </div>
                        {!e.custom && (
                        <>
                        <div className="text-xs text-[var(--mep-text-muted)] uppercase tracking-[0.15em] mt-1">
                          {MUSCLE_LABEL[e.muscle] || e.muscle}
                        </div>
                        <div className="grid grid-cols-3 gap-3 mt-3">
                          <Stat icon={Dumbbell} label="Series" value={e.sets} />
                          <Stat icon={Repeat} label="Reps" value={e.reps} />
                          <Stat icon={Clock} label="Descanso" value={e.rest_sec > 0 ? `${e.rest_sec}s` : "—"} />
                        </div>
                        </>
                        )}
                      </div>
                    </div>

                    {/* Última sesión */}
                    {last && (
                      <div
                        data-testid={`last-session-${e.id}`}
                        className="mt-4 p-3 rounded-lg bg-[var(--mep-elev)] border-l-2 border-[var(--mep-gold)]"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <History size={13} className="text-[var(--mep-gold)]" />
                          <div className="text-eyebrow text-[var(--mep-gold)]">Última sesión</div>
                          <div className="text-[11px] text-[var(--mep-text-muted)] ml-auto">
                            {dayNameEs(last.recorded_at)} · {fmtDate(last.recorded_at)}
                          </div>
                        </div>
                        <div className="text-sm">
                          {last.cardio_rounds ? (
                            <>
                              <span className="font-medium">{last.cardio_rounds} rounds</span>
                              <span className="text-[var(--mep-text-muted)]"> × </span>
                              <span>{last.cardio_duration_sec}s</span>
                              {last.cardio_rest_sec != null && (
                                <> <span className="text-[var(--mep-text-muted)]">· descanso {last.cardio_rest_sec}s</span></>
                              )}
                            </>
                          ) : e.kind === "cardio_rounds" ? (
                            <span data-testid={`last-session-saco-summary-${e.id}`}>
                              <span className="font-medium">{last.sets} rounds</span>
                              <span className="text-[var(--mep-text-muted)]">
                                {(() => { try { return JSON.parse(last.reps_per_set); } catch { return []; } })().map((s) => ` · ${s}s`).join("")}
                                {last.cardio_rest_sec != null && ` · descanso ${last.cardio_rest_sec}s`}
                              </span>
                            </span>
                          ) : last.is_bodyweight ? (
                            <>
                              <span className="font-medium text-[var(--mep-gold)]">Peso corporal</span>
                              <span className="text-[var(--mep-text-muted)]"> · {last.sets} {last.sets === 1 ? "serie" : "series"} · reps </span>
                              <span>{(() => { try { return JSON.parse(last.reps_per_set).join("-"); } catch { return last.reps_per_set; } })()}</span>
                            </>
                          ) : (
                            <>
                              {last.weight_kg != null && <span className="font-medium">{last.weight_kg} kg</span>}
                              {last.weight_kg != null && <span className="text-[var(--mep-text-muted)]"> · </span>}
                              <span className="text-[var(--mep-text-muted)]">{last.sets} {last.sets === 1 ? "serie" : "series"}</span>
                              <span className="text-[var(--mep-text-muted)]"> · reps </span>
                              <span>{(() => { try { return JSON.parse(last.reps_per_set).join("-"); } catch { return last.reps_per_set; } })()}</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => setLoggingFor(e)}
                      data-testid={`register-set-${e.id}`}
                      className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[var(--mep-pink)] text-white font-medium text-sm hover:brightness-110 transition"
                    >
                      <Plus size={14} /> Registrar serie
                    </button>
                  </article>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setCustomForm({ muscle: m })}
              data-testid={`add-custom-exercise-${m}`}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-[var(--mep-gold)]/60 text-sm text-[var(--mep-gold)] hover:bg-[rgba(212,175,55,0.08)] transition-colors"
            >
              <Plus size={14} /> Agregar ejercicio
            </button>
          </TabsContent>
        ))}
      </Tabs>

      {customForm && (
        <CustomExerciseModal
          initial={customForm}
          onClose={() => setCustomForm(null)}
          onSaved={() => { setCustomForm(null); loadCustoms(); refreshLastSessions(); }}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-[var(--mep-surface)] border-[var(--mep-border)]" data-testid="custom-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este ejercicio personalizado?</AlertDialogTitle>
            <AlertDialogDescription>
              Se quitará "{deleting?.name}" de tus rutinas. Tus registros ya guardados en Mi libreta de entrenamiento se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="custom-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={removeCustom} data-testid="custom-delete-confirm" className="bg-[var(--mep-pink)] hover:brightness-110">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {loggingFor && (
        <RegisterSetModal
          exercise={loggingFor}
          routineId={sex === "mujer" ? "rutinas-mujeres" : "rutinas-hombres"}
          onClose={() => setLoggingFor(null)}
          onSaved={async () => {
            await refreshLastSessions();
            setLoggingFor(null);
          }}
        />
      )}
    </AppShell>
  );
}

function CustomExerciseModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial.name || "");
  const [saving, setSaving] = useState(false);
  const save = async (ev) => {
    ev.preventDefault();
    if (saving || name.trim().length < 2) return;
    setSaving(true);
    try {
      if (initial.id) await api.patch(`/custom-exercises/${initial.id}`, { name: name.trim() });
      else await api.post("/custom-exercises", { muscle: initial.muscle, name: name.trim() });
      toast.success(initial.id ? "Ejercicio actualizado" : "Ejercicio agregado");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="custom-exercise-modal">
      <form onSubmit={save} onClick={(ev) => ev.stopPropagation()} className="mep-card w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl">{initial.id ? "Editar ejercicio" : "Agregar ejercicio"}</h3>
          <button type="button" onClick={onClose} className="text-[var(--mep-text-muted)] hover:text-white p-1" aria-label="Cerrar"><X size={16} /></button>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block" htmlFor="custom-exercise-name">Nombre del ejercicio</label>
          <input
            id="custom-exercise-name" autoFocus maxLength={120} value={name} onChange={(ev) => setName(ev.target.value)}
            placeholder="Ej. Extensión de tríceps con banda" data-testid="custom-exercise-name-input"
            className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-pink)]"
          />
        </div>
        <button
          type="submit" disabled={saving || name.trim().length < 2} data-testid="custom-exercise-save"
          className="w-full py-2.5 rounded-lg bg-[var(--mep-pink)] text-white font-medium text-sm hover:brightness-110 disabled:opacity-60"
        >
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </form>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1.5 text-[var(--mep-text-muted)] text-[10px] uppercase tracking-[0.14em]">
      <Icon size={11} className="text-[var(--mep-gold)]" /> {label}
    </div>
    <div className="font-display text-sm md:text-base font-medium mt-1 text-white">{value}</div>
  </div>
);

function RegisterSetModal({ exercise, routineId, onClose, onSaved }) {
  const isCardioRounds = exercise.kind === "cardio_rounds";
  const defaultBodyweight = !isCardioRounds && !!exercise.bodyweight_default;

  const defaultSets = isCardioRounds ? 3 : Math.max(1, Math.min(6, exercise.sets || 3));
  const [reps, setReps] = useState(Array(defaultSets).fill(""));
  const [obs, setObs] = useState(Array(defaultSets).fill(""));
  const [weight, setWeight] = useState("");
  const [bodyweight, setBodyweight] = useState(defaultBodyweight);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [restSec, setRestSec] = useState("60");
  const rowLabel = isCardioRounds ? "Round" : "Serie";

  const addSet = () => {
    if (reps.length >= 10) return;
    setReps((r) => [...r, ""]);
    setObs((o) => [...o, ""]);
  };
  const removeSet = (i) => {
    if (reps.length <= 1) return;
    setReps((r) => r.filter((_, idx) => idx !== i));
    setObs((o) => o.filter((_, idx) => idx !== i));
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { exercise_id: exercise.id };
    if (routineId) payload.routine_id = routineId;
    if (isCardioRounds) {
      const valid = reps
        .map((v, i) => ({ n: parseInt(v, 10), o: (obs[i] || "").trim() }))
        .filter((x) => Number.isFinite(x.n) && x.n > 0);
      if (valid.length === 0) { toast.error("Indica la duración de al menos un round."); return; }
      payload.reps_per_set = valid.map((x) => x.n);
      payload.is_bodyweight = true;
      const rs = parseInt(restSec, 10);
      if (Number.isFinite(rs) && rs >= 0) payload.cardio_rest_sec = rs;
      const obsText = valid.map((x, i) => (x.o ? `R${i + 1}: ${x.o}` : null)).filter(Boolean).join(" · ");
      const fullNote = [note.trim(), obsText].filter(Boolean).join(" — ");
      if (fullNote) payload.note = fullNote;
    } else {
      if (note) payload.note = note;
      const parsed = reps.map((v) => parseInt(v, 10)).filter((n) => Number.isFinite(n) && n > 0);
      if (parsed.length === 0) { toast.error("Indica al menos una serie con repeticiones."); return; }
      payload.reps_per_set = parsed;
      if (bodyweight) {
        payload.is_bodyweight = true;
      } else if (weight !== "") {
        payload.weight_kg = parseFloat(weight);
      }
    }

    setSaving(true);
    try {
      await api.post("/training-log", payload);
      toast.success("Serie registrada");
      await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="register-set-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--mep-surface)] border border-[var(--mep-border)] w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-eyebrow text-[var(--mep-pink)]">Registrar serie</div>
            <h3 className="font-display text-xl mt-1">{exercise.name}</h3>
            <div className="text-xs text-[var(--mep-text-muted)] uppercase tracking-[0.15em] mt-0.5">
              {MUSCLE_LABEL[exercise.muscle] || exercise.muscle}
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--mep-text-muted)] hover:text-white p-1" data-testid="register-set-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <>
            {isCardioRounds ? (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Modo</label>
                <div
                  data-testid="mode-bodyweight-fixed"
                  className="w-full px-3 py-2 rounded-lg text-sm border text-center bg-[var(--mep-gold)] text-black border-[var(--mep-gold)]"
                >
                  Peso corporal
                </div>
              </div>
            ) : (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Modo</label>
                <div className="flex gap-2" data-testid="weight-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setBodyweight(false)}
                    data-testid="mode-external"
                    className={`flex-1 px-3 py-2 rounded-lg text-sm border transition ${!bodyweight ? "bg-[var(--mep-pink)] text-white border-[var(--mep-pink)]" : "border-[var(--mep-border)] text-[var(--mep-text-muted)] hover:border-[var(--mep-pink)]/40"}`}
                  >
                    Peso externo
                  </button>
                  <button
                    type="button"
                    onClick={() => setBodyweight(true)}
                    data-testid="mode-bodyweight"
                    className={`flex-1 px-3 py-2 rounded-lg text-sm border transition ${bodyweight ? "bg-[var(--mep-gold)] text-black border-[var(--mep-gold)]" : "border-[var(--mep-border)] text-[var(--mep-text-muted)] hover:border-[var(--mep-gold)]/40"}`}
                  >
                    Peso corporal
                  </button>
                </div>
              </div>
            )}

              {!isCardioRounds && !bodyweight && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">
                    Peso (kg)
                  </label>
                  <input
                    data-testid="register-set-weight"
                    type="number" step="0.5" value={weight} onChange={(ev) => setWeight(ev.target.value)}
                    placeholder="Ej. 40"
                    className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)]"
                  />
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)]">
                    {isCardioRounds ? "Duración (s) por round" : "Reps por serie"}
                  </label>
                  <button type="button" onClick={addSet} data-testid="register-add-set" className="text-xs text-[var(--mep-pink)] hover:underline">
                    + Añadir {isCardioRounds ? "round" : "serie"}
                  </button>
                </div>
                <div className="space-y-2">
                  {reps.map((v, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`register-row-${i}`}>
                      <div className="w-16 text-[11px] uppercase tracking-[0.14em] text-[var(--mep-text-muted)]">{rowLabel} {i + 1}</div>
                      <input
                        data-testid={`register-reps-${i}`}
                        type="number" min="1" max="999" value={v}
                        onChange={(ev) => { const copy = [...reps]; copy[i] = ev.target.value; setReps(copy); }}
                        placeholder={isCardioRounds ? "120" : "12"}
                        className={`${isCardioRounds ? "w-20" : "flex-1"} min-w-0 bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 focus:outline-none focus:border-[var(--mep-pink)]`}
                      />
                      {isCardioRounds && (
                        <input
                          data-testid={`register-obs-${i}`}
                          type="text" maxLength={40} value={obs[i] || ""}
                          onChange={(ev) => { const copy = [...obs]; copy[i] = ev.target.value; setObs(copy); }}
                          placeholder="Ej. combos rápidos"
                          className="flex-1 min-w-0 bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[var(--mep-pink)]"
                        />
                      )}
                      {reps.length > 1 && (
                        <button type="button" onClick={() => removeSet(i)} data-testid={`register-remove-${i}`} className="text-[var(--mep-text-muted)] hover:text-[var(--mep-pink)] p-1">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {isCardioRounds && (
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">
                    Descanso entre rounds (s)
                  </label>
                  <input
                    data-testid="register-rest"
                    type="number" min="0" step="5" value={restSec} onChange={(ev) => setRestSec(ev.target.value)}
                    placeholder="60"
                    className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)]"
                  />
                </div>
              )}
          </>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">
              Nota (opcional)
            </label>
            <input
              data-testid="register-set-note"
              type="text" value={note} onChange={(ev) => setNote(ev.target.value)}
              placeholder="Ej. Buena técnica, bajar la carga la próxima"
              className="w-full bg-[var(--mep-surface-2)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)]"
            />
          </div>

          <button
            type="submit" disabled={saving}
            data-testid="register-set-submit"
            className="w-full py-3 rounded-lg bg-[var(--mep-pink)] text-white font-semibold hover:brightness-110 transition disabled:opacity-50"
          >
            {saving ? "Guardando..." : "Guardar en mi libreta"}
          </button>
        </form>
      </div>
    </div>
  );
}
